import {getFormattedDateTime} from "@ots-frontend/ots-data";
import {OtsDateTime} from "../components/OtsDateTime";
import {billingEntityName} from "./billing-decision-display";
import {OtsBadge} from "../components/OtsBadge";
import type {BillingDecisionTrace} from "./ticket-cost-types";

const date = (value: number | null) => value == null || value === 0 ? "Open-ended" : getFormattedDateTime(value);

const colors: Record<string, string> = {
  selected: "bg-[var(--ots-success-container)] border-[var(--ots-success-outline)]",
  rejected: "bg-[var(--ots-danger-container)] border-[var(--ots-danger-outline)]",
};

export const BillingScheduleTimeline = ({trace}: {trace: BillingDecisionTrace}) => {
  const intervals = trace.intervals.filter((interval, index, all) => all.findIndex(other =>
    other.wellId === interval.wellId && other.eventId === interval.eventId && other.stageId === interval.stageId &&
    other.startDttm === interval.startDttm && other.endDttm === interval.endDttm && other.outcome === interval.outcome) === index);
  const time = trace.effectiveDttm;
  const finite = intervals.flatMap(interval => [interval.startDttm, interval.endDttm])
    .filter((value): value is number => value != null && Number.isFinite(value) && value > 0);
  if (time != null && Number.isFinite(time)) finite.push(time);
  const first = finite.length ? Math.min(...finite) : 0;
  const last = finite.length ? Math.max(...finite) : first + 1;
  const padding = Math.max((last - first) * 0.05, 60_000);
  const min = first - padding;
  const max = last + padding;
  const percent = (value: number) => Math.max(0, Math.min(100, (value - min) / (max - min) * 100));
  const wells = [...new Set(intervals.map(interval => interval.wellId))];
  if (!intervals.length) return <p className="text-sm text-[var(--ots-text-muted)]">No schedule intervals were evaluated.</p>;

  return <section aria-label="Well stage effectivity timeline" className="space-y-4">
    <p className="text-xs text-[var(--ots-text-muted)]">{time == null ? "No ticket timestamp was provided." : `Ticket time: ${date(time)}`}</p>
    <div className="flex justify-between gap-2 text-[10px] text-[var(--ots-text-muted)]"><span>{date(min)}</span><span>{date(max)}</span></div>
    {wells.map(wellId => <div key={wellId}>
      <h4 className="mb-2 break-all text-sm font-semibold">{billingEntityName(trace, wellId, "Well name unavailable")}</h4>
      <div className="relative rounded-lg bg-[var(--ots-surface-variant)] p-1">
        {intervals.filter(interval => interval.wellId === wellId).map((interval, index) => {
          const reason = trace.steps.find(step => step.entityId === wellId && step.outcome === "rejected" &&
            step.code.startsWith("STAGE_") && step.code !== "STAGE_AFE_PERMISSION" &&
            (!step.values.stageId || step.values.stageId === interval.stageId));
          return <div key={index} className="relative mb-1 h-8 last:mb-0" role="img"
            aria-label={`${interval.stageName ?? billingEntityName(trace, interval.stageId, "Stage name unavailable")}: ${date(interval.startDttm)} to ${date(interval.endDttm)}, ${interval.outcome}${interval.outcome === "rejected" && reason ? `: ${reason.explanation}` : ""}`}>
            <div className={`absolute inset-y-0 overflow-hidden rounded border px-2 text-xs leading-7 ${interval.synthetic ? "border-dashed" : "border-solid"} ${colors[interval.outcome] ?? "bg-[var(--ots-primary-container)] border-[var(--ots-primary)]"}`}
              style={{left: `${interval.startDttm == null || interval.startDttm === 0 ? 0 : percent(interval.startDttm)}%`,
                right: `${interval.endDttm == null ? 0 : 100 - percent(interval.endDttm)}%`}}>
              <span className="whitespace-nowrap">{interval.stageName ?? billingEntityName(trace, interval.stageId, "Stage name unavailable")}</span>
            </div>
          </div>;
        })}
        {time != null && <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 z-10 border-l-2 border-[var(--ots-text)]" style={{left: `${percent(time)}%`}} />}
      </div>
      <ul className="mt-2 space-y-1 text-xs">
        {intervals.filter(interval => interval.wellId === wellId).map((interval, index) => {
          const reason = interval.outcome === "rejected" ? trace.steps.find(step => step.entityId === wellId &&
            step.outcome === "rejected" && step.code.startsWith("STAGE_") && step.code !== "STAGE_AFE_PERMISSION" &&
            (!step.values.stageId || step.values.stageId === interval.stageId)) : undefined;
          return <li key={index} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <OtsBadge size="compact" tone={interval.outcome === "selected" ? "success" : interval.outcome === "rejected" ? "danger" : "info"}>{interval.outcome === "rejected" ? "Excluded interval" : interval.outcome === "selected" ? "Selected" : "Eligible"}</OtsBadge>
              <span>{interval.stageName ?? billingEntityName(trace, interval.stageId, "Stage name unavailable")}{interval.synthetic ? " (default)" : ""}</span>
              <span className="text-[var(--ots-text-muted)]"><OtsDateTime value={interval.startDttm || undefined} empty="Open-ended" /> → <OtsDateTime value={interval.endDttm || undefined} empty="Open-ended" /></span>
            </div>
            {reason && <p className="text-[var(--ots-text-muted)]">{reason.explanation}</p>}
          </li>;
        })}
      </ul>
    </div>)}
    <p className="text-xs text-[var(--ots-text-muted)]">Vertical line: ticket time. Dashed intervals: synthetic defaults. Dates use the selected region’s timezone.</p>
  </section>;
};
